import { memo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
   Button,
   Div,
   Divider,
   Dropdown,
   filterHover,
   Form,
   getFormErrorObject,
   InputField,
   PageHeader,
   Text,
   useBooleanState,
   useForm,
   useMediaQuery,
   useTheme,
} from "react-better-html";

import { authLoginHint, authSignUpHint } from "../../constants/app";

import { useQuery, useReduxAction } from "../../utils/hooks";
import { passwordIsValid } from "../../utils/functions";
import { useI18n } from "../../utils/i18n";

import AppLogo from "../../components/AppLogo";
import CenterContentPageHolder from "../../components/structure/CenterContentPageHolder";

const pathAfterAuth: string = "/";

function Auth() {
   const theme = useTheme();
   const mediaQuery = useMediaQuery();
   const query = useQuery();
   const navigate = useNavigate();
   const { t } = useI18n();

   const signUpWithEmailAndPassword = useReduxAction((actions) => actions.user.signUpWithEmailAndPassword);
   const loginWithEmailAndPassword = useReduxAction((actions) => actions.user.loginWithEmailAndPassword);
   const loginWithProvider = useReduxAction((actions) => actions.user.loginWithProvider);

   const [isSignUp, setIsSignUp] = useBooleanState(query["hint"] === authSignUpHint);

   const form = useForm({
      defaultValues: {
         fullName: "",
         email: "",
         password: "",
      },
      requiredFields: isSignUp ? ["fullName", "email", "password"] : ["email", "password"],
      validate: (values) => {
         const errors = getFormErrorObject(values);

         if (!passwordIsValid(values.password)) {
            errors.password = t((l) => l.components.form.passwordRule);
         }

         return errors;
      },
      onSubmit: (values) => {
         if (isSignUp) {
            signUpWithEmailAndPassword({
               fullName: values.fullName,
               email: values.email,
               password: values.password,
               onSuccess: () => {
                  navigate(pathAfterAuth);
               },
            });
         } else {
            loginWithEmailAndPassword({
               email: values.email,
               password: values.password,
               onSuccess: () => {
                  navigate(pathAfterAuth);
               },
            });
         }
      },
   });

   const onClickHintText = useCallback(() => {
      const newValue = !isSignUp;

      setIsSignUp.setState(newValue);

      if (newValue) navigate(`?hint=${authSignUpHint}`);
      else navigate(`?hint=${authLoginHint}`);
   }, [isSignUp]);
   const onClickWithGoogle = useCallback(() => {
      loginWithProvider({
         provider: "google",
         onSuccess: () => {
            navigate(pathAfterAuth);
         },
      });
   }, []);

   return (
      <>
         <CenterContentPageHolder.content>
            <Div.row
               width="100%"
               alignItems="center"
               justifyContent="space-between"
               marginBottom={theme.styles.space * 2}
            >
               <AppLogo />

               <Dropdown.language isSmall />
            </Div.row>

            <PageHeader
               title={isSignUp ? t((l) => l.pages.auth.register.title) : t((l) => l.pages.auth.login.title)}
               description={
                  isSignUp ? t((l) => l.pages.auth.register.description) : t((l) => l.pages.auth.login.description)
               }
            />

            <Form form={form} gap={theme.styles.space}>
               <Div.column gap={theme.styles.gap}>
                  {isSignUp && (
                     <InputField
                        label={t((l) => l.common.fullName)}
                        backgroundColor={theme.colors.backgroundBase}
                        {...form.getInputFieldProps("fullName")}
                     />
                  )}
                  <InputField.email
                     label={t((l) => l.common.email)}
                     backgroundColor={theme.colors.backgroundBase}
                     {...form.getInputFieldProps("email")}
                  />
                  <InputField.password
                     label={t((l) => l.common.password)}
                     backgroundColor={theme.colors.backgroundBase}
                     infoText={t((l) => l.components.form.passwordRule)}
                     {...form.getInputFieldProps("password")}
                  />
               </Div.column>

               <Button
                  text={isSignUp ? t((l) => l.pages.auth.register.createAccount) : t((l) => l.pages.auth.login.logIn)}
                  loaderName={isSignUp ? "user:signUpWithEmailAndPassword" : "user:loginWithEmailAndPassword"}
                  type="submit"
                  width="100%"
                  backgroundColor={theme.colors.textPrimary}
               />

               {isSignUp && (
                  <Text fontSize={12} textAlign="center" color={theme.colors.textSecondary}>
                     {t((l) => l.pages.auth.register.agreeToText, {
                        termsAndConditions: (text) => (
                           <Link to="/legal/terms-and-conditions">
                              <Text
                                 as="span"
                                 fontWeight={700}
                                 color={theme.colors.primary}
                                 filterHover={filterHover().z1}
                              >
                                 {text}
                              </Text>
                           </Link>
                        ),
                        privacyPolicy: (text) => (
                           <Link to="/legal/privacy-policy">
                              <Text
                                 as="span"
                                 fontWeight={700}
                                 color={theme.colors.primary}
                                 filterHover={filterHover().z1}
                              >
                                 {text}
                              </Text>
                           </Link>
                        ),
                     })}
                  </Text>
               )}

               <Divider.horizontal
                  text={t((l) => l.common.or)}
                  marginBlock={!mediaQuery.size600 ? theme.styles.space : undefined}
               />

               <Button.secondary
                  text={t((l) => l.pages.auth.continueWithGoogle)}
                  loaderName="user:loginWithProvider"
                  icon="googleG"
                  width="100%"
                  onClick={onClickWithGoogle}
               />

               <Text fontSize={14} textAlign="center" color={theme.colors.textSecondary}>
                  {isSignUp
                     ? t((l) => l.pages.auth.register.alreadyHaveAccount)
                     : t((l) => l.pages.auth.login.doNotHaveAccount)}{" "}
                  <Text
                     as="span"
                     fontWeight={700}
                     color={theme.colors.primary}
                     filterHover={filterHover().z1}
                     userSelect="none"
                     isTabAccessed
                     cursor="pointer"
                     onClick={onClickHintText}
                  >
                     {isSignUp ? t((l) => l.pages.auth.login.logIn) : t((l) => l.pages.auth.register.createAccount)}
                  </Text>
               </Text>
            </Form>
         </CenterContentPageHolder.content>

         <CenterContentPageHolder.decoration marketingText={t((l) => l.pages.auth.marketingText)} />
      </>
   );
}

export default memo(Auth);
